import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Play, X, FileCheck2, Video } from "lucide-react";
import { api, REGISTER_URL } from "@/lib/api";
import { Reveal, SectionHead, Wrap } from "./Motion";

const filters = [["all", "All", "all"], ["grand_prix", "Grand Prix", "grand-prix"], ["young_rider", "Young Rider", "young-rider"], ["amateur", "Amateur", "amateur"], ["pony", "Pony", "pony"], ["investment", "Investment", "investment"]];

const HorseCard = ({ h, onOpen }) => (
  <article className="group relative h-full bg-[#0F1D3D] border border-white/8 card-hover cursor-pointer" data-testid={`ballypatrick-horse-card-${h.lot}`} onClick={() => onOpen(h)}>
    <div className="relative aspect-[4/5] overflow-hidden">
      <img src={h.image} alt={`${h.name}, ${h.born} ${h.colour} ${h.sex}`} className="h-full w-full object-cover" loading="lazy" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0B1630] via-[#0B1630]/20 to-transparent" />
      <span className="absolute top-4 left-4 font-mono text-[10px] tracking-[0.2em] text-[#F8F6F0]/80">LOT {String(h.lot).padStart(2, "0")}</span>
      <span className="absolute top-4 right-4 bg-[#4B7A38]/30 backdrop-blur text-[#CFE9C2] border border-[#6FAE55]/40 px-2.5 py-1 text-[9px] font-mono uppercase tracking-wider" data-testid={`horse-level-badge-${h.lot}`}>{h.level_label}</span>
      <div className="absolute bottom-0 inset-x-0 p-5">
        <p className="text-[10px] tracking-[0.18em] uppercase text-[#AEB7C9]">{h.born} · {h.colour} {h.sex}</p>
        <h3 className="mt-1 font-display text-2xl text-[#F8F6F0] leading-tight">{h.name}</h3>
        <p className="mt-1 text-xs text-[#6F7C97]">{h.sire} × {h.dam_sire}</p>
      </div>
    </div>
    <div className="px-5 py-4 flex items-center justify-between border-t border-white/5">
      <div>
        <p className="text-[9px] tracking-[0.2em] uppercase text-[#6F7C97]">Opening bid</p>
        <p className="font-display text-lg text-[#F8F6F0]">€{h.starting_price.toLocaleString()}</p>
      </div>
      <button className="text-[10px] tracking-[0.18em] uppercase text-[#D4AF37] link-line" data-testid={`ballypatrick-horse-modal-open-${h.lot}`}>Expert view</button>
    </div>
  </article>
);

const HorseModal = ({ h, onClose }) => {
  useEffect(() => {
    const k = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", k);
    document.documentElement.classList.add("lenis-stopped");
    return () => { window.removeEventListener("keydown", k); document.documentElement.classList.remove("lenis-stopped"); };
  }, [onClose]);
  return (
    <motion.div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center sm:p-6" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="horse-modal" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-[#0B1630]/85 backdrop-blur-md" onClick={onClose} />
      <motion.div initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }} transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full max-w-5xl max-h-[92svh] overflow-y-auto bg-[#0F1D3D] border border-[#D4AF37]/25 grid lg:grid-cols-2" data-lenis-prevent>
        <button onClick={onClose} className="sticky top-3 lg:absolute lg:top-4 right-3 lg:right-4 z-10 ml-auto mr-3 lg:mr-0 mt-3 lg:mt-0 text-[#F8F6F0] bg-[#0B1630]/80 p-2 hover:text-[#D4AF37] transition-colors" data-testid="horse-modal-close" aria-label="Close"><X size={18} /></button>
        <div className="relative aspect-[4/3] sm:aspect-[4/5] lg:aspect-auto lg:min-h-[560px] -mt-12 lg:mt-0">
          <img src={h.image} alt={h.name} className="absolute inset-0 h-full w-full object-cover" />
          {h.video && <div className="absolute inset-0 flex items-center justify-center"><span className="h-14 w-14 rounded-full border border-[#D4AF37]/60 bg-[#0B1630]/40 backdrop-blur flex items-center justify-center text-[#D4AF37]" data-testid="horse-modal-video-btn"><Play size={18} fill="currentColor" /></span></div>}
        </div>
        <div className="p-6 sm:p-8 lg:p-12">
          <p className="eyebrow">Lot {String(h.lot).padStart(2, "0")} · {h.collection} collection</p>
          <h3 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl text-[#F8F6F0] leading-none" data-testid="horse-modal-name">{h.name}</h3>
          <p className="mt-2 text-sm text-[#AEB7C9]">{h.born} · {h.colour} {h.sex} · {h.height} · {h.sire} × {h.dam_sire}</p>
          <div className="mt-5 flex flex-wrap gap-2">
            <span className="bg-[#4B7A38]/25 text-[#CFE9C2] border border-[#6FAE55]/40 px-3 py-1.5 text-[10px] font-mono uppercase tracking-wider">Suited to · {h.level_label}</span>
            <span className="inline-flex items-center gap-1.5 border border-white/15 text-[#AEB7C9] px-3 py-1.5 text-[10px] font-mono uppercase tracking-wider"><FileCheck2 size={12} /> X-rays · {h.xrays}</span>
            {h.video && <span className="inline-flex items-center gap-1.5 border border-white/15 text-[#AEB7C9] px-3 py-1.5 text-[10px] font-mono uppercase tracking-wider"><Video size={12} /> Ridden video</span>}
          </div>
          <p className="mt-6 text-sm text-[#AEB7C9] leading-relaxed">{h.description}</p>
          <div className="mt-6 border-l border-[#D4AF37] pl-4">
            <p className="text-[10px] tracking-[0.2em] uppercase text-[#D4AF37]">Greg Broderick's assessment</p>
            <p className="mt-2 font-display text-lg sm:text-xl italic text-[#F8F6F0] leading-snug" data-testid="horse-modal-assessment">"{h.assessment}"</p>
          </div>
          <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-3">
            <a href={REGISTER_URL} target="_blank" rel="noreferrer" className="btn-gold" data-testid="horse-modal-register-btn">Register to bid <ArrowUpRight size={14} /></a>
            <p className="text-xs text-[#6F7C97]">Opening bid €{h.starting_price.toLocaleString()}</p>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export const Collection = () => {
  const [horses, setHorses] = useState([]);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(null);
  useEffect(() => { api.get("/horses").then((r) => setHorses(r.data)).catch(() => {}); }, []);
  const shown = filter === "all" ? horses : horses.filter((h) => h.level_key === filter);

  return (
    <section id="collection" className="relative py-20 lg:py-36" data-testid="collection-section">
      <Wrap>
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <SectionHead eyebrow="The Collection · 2026 preview" id="collection" title="Find the horse for your level, not just your budget." sub="Reference horses, each tagged with the level it's produced to win at. Full 2026 catalogue revealed in November." />
          <Reveal delay={0.15}>
            <div className="snap-row lg:flex-wrap lg:overflow-visible -mx-5 px-5 lg:mx-0 lg:px-0 gap-2" data-testid="horse-filters">
              {filters.map(([k, label, id]) => (
                <button key={k} onClick={() => setFilter(k)} data-testid={`ballypatrick-horse-filter-${id}`}
                  className={`px-4 py-2 text-[10px] tracking-[0.18em] uppercase border transition-all duration-300 ${filter === k ? "bg-[#D4AF37] text-[#0B1630] border-[#D4AF37]" : "border-white/15 text-[#AEB7C9] hover:border-[#D4AF37]/50 hover:text-[#F8F6F0]"}`}>{label}</button>
              ))}
            </div>
          </Reveal>
        </div>

        <div className="mt-10 snap-row lg:hidden -mx-5 px-5" data-testid="horse-snap-row">
          {shown.map((h) => <div key={h.id} className="w-[76vw] max-w-[340px]"><HorseCard h={h} onOpen={setOpen} /></div>)}
        </div>
        <motion.div layout className="mt-14 hidden lg:grid grid-cols-3 xl:grid-cols-4 gap-6" data-testid="horse-grid">
          <AnimatePresence mode="popLayout">
            {shown.map((h, i) => (
              <motion.div key={h.id} layout initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, scale: 0.97 }} transition={{ duration: 0.5, delay: i * 0.04 }}>
                <HorseCard h={h} onOpen={setOpen} />
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
        {shown.length === 0 && horses.length > 0 && <p className="mt-10 text-sm text-[#6F7C97]" data-testid="horse-empty">No lots in this category yet.</p>}
      </Wrap>
      <AnimatePresence>{open && <HorseModal h={open} onClose={() => setOpen(null)} />}</AnimatePresence>
    </section>
  );
};
